import { useState, type FormEvent } from "react";
import {
  login,
  verifyTotp,
  adLogin,
  requestPasswordReset,
  submitPasswordReset,
  type AuthMode,
} from "../auth/webAuth";
import { getTigerResource } from "../utils/tigerResource";

interface Props {
  mode: AuthMode;
  onAuthed: () => void;
  /** Present when the user arrived from an emailed reset link (`?reset_token=`).
   *  Shows the choose-a-new-password form instead of the sign-in form. */
  resetToken?: string;
  /** Called once a reset succeeds and the user asks to sign in, so the caller can
   *  drop `?reset_token=` from the URL. Defaults to stripping it in place. */
  onResetFinished?: () => void;
}

/** Remove `?reset_token=` from the address bar so the spent token is not left in
 *  history, bookmarks, or a copied URL. */
function stripResetTokenFromUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete("reset_token");
  window.history.replaceState({}, "", url.toString());
}

const overlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(20,24,40,0.6)",
  backdropFilter: "blur(12px)",
};

const card: React.CSSProperties = {
  minWidth: 320,
  padding: 28,
  borderRadius: 12,
  background: "rgba(255,255,255,0.12)",
  backdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.25)",
  boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
  color: "#fff",
  display: "flex",
  flexDirection: "column",
  gap: 14,
};

// The Tiger logo (same asset as the C# TigerWebSite login). It's transparent
// black line art, so it sits on a white rounded badge to stay legible on the
// dark acrylic card (which also echoes the C# white-card presentation).
const logoBadge: React.CSSProperties = {
  alignSelf: "center",
  width: 112,
  height: 112,
  borderRadius: 16,
  background: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
  marginBottom: 2,
};
const logoImg: React.CSSProperties = { width: 92, height: "auto", display: "block" };

const TigerLogo = (
  <div style={logoBadge}>
    <img src="/tiger_logo.png" alt={getTigerResource("TIGER_LOGIN_LOGO_ALT")} style={logoImg} />
  </div>
);

const field: React.CSSProperties = { padding: 8, borderRadius: 6, border: "none" };
const primaryBtn: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: 6,
  border: "none",
  background: "var(--ui-accent)",
  color: "#fff",
  fontWeight: 600,
  cursor: "pointer",
};
const labelCol: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const errorText: React.CSSProperties = { color: "#ffb4b4" };
/** A text-only affordance (forgot-password / back-to-sign-in), styled as a link
 *  but a real button so it is keyboard-reachable and announced correctly. */
const linkBtn: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  color: "#cdd6ff",
  textDecoration: "underline",
  cursor: "pointer",
  font: "inherit",
  alignSelf: "center",
};

/** Phase 1/2: username+password for db_auth/mfa_auth, with a TOTP code step when
 *  the backend responds `mfa_required`. (AD variant added in Phase 3.) Styled to
 *  match the acrylic-glass dialog theme. */
export function LoginScreen({ mode, onAuthed, resetToken, onResetFinished }: Props) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState("");
  // "Remember this device": skip the code prompt on later logins from this
  // browser. On by default — untick it on a shared machine.
  const [rememberDevice, setRememberDevice] = useState(true);

  // Password-reset sub-states. `view` is "signin" unless the user asked to
  // reset, or arrived on a reset link.
  const [view, setView] = useState<"signin" | "forgot" | "sent">("signin");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [resetDone, setResetDone] = useState(false);

  // Reset is a db_auth/mfa_auth concept: under ad_auth the directory owns the
  // password, and there is no UserManager to reset it against.
  const resetSupported = mode === "db_auth" || mode === "mfa_auth";

  async function onForgotSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const err = await requestPasswordReset(username);
    setBusy(false);
    if (err) setError(err);
    // Always confirm, whatever the outcome: the backend does not disclose
    // whether the account exists, and neither should the UI.
    else setView("sent");
  }

  async function onResetSubmit(e: FormEvent) {
    e.preventDefault();
    if (!resetToken) return;
    // Checked client-side too, to save a round trip on an obvious typo.
    if (newPassword !== confirm) {
      setErrors([getTigerResource("TIGER_LOGIN_PWD_MISMATCH")]);
      return;
    }
    setBusy(true);
    setErrors([]);
    const errs = await submitPasswordReset(resetToken, newPassword, confirm);
    setBusy(false);
    if (errs.length) setErrors(errs);
    else setResetDone(true);
  }

  async function onPasswordSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    if (mode === "ad_auth") {
      const err = await adLogin(username, password);
      setBusy(false);
      if (err) setError(err);
      else onAuthed();
      return;
    }
    const outcome = await login(username, password);
    setBusy(false);
    if (outcome.kind === "ok") onAuthed();
    else if (outcome.kind === "mfa") setChallenge(outcome.challenge);
    else setError(outcome.message);
  }

  async function onCodeSubmit(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setBusy(true);
    setError(null);
    const err = await verifyTotp(challenge, code, rememberDevice);
    setBusy(false);
    if (err) setError(err);
    else onAuthed();
  }

  // Arrived on an emailed reset link: choose a new password. Takes precedence
  // over the sign-in form, since the user is by definition not signed in.
  if (resetToken && resetSupported) {
    if (resetDone) {
      return (
        <div style={overlay}>
          <div style={card}>
            {TigerLogo}
            <div>{getTigerResource("TIGER_LOGIN_RESET_DONE")}</div>
            <button
              type="button"
              style={primaryBtn}
              onClick={() => {
                stripResetTokenFromUrl();
                onResetFinished?.();
              }}
            >
              {getTigerResource("TIGER_LOGIN_SIGN_IN")}
            </button>
          </div>
        </div>
      );
    }
    return (
      <div style={overlay}>
        <form onSubmit={onResetSubmit} style={card}>
          {TigerLogo}
          <h2 style={{ margin: 0, fontWeight: 600, textAlign: "center" }}>
            {getTigerResource("TIGER_LOGIN_RESET_TITLE")}
          </h2>
          <label style={labelCol}>
            {getTigerResource("TIGER_LOGIN_NEW_PASSWORD")}
            <input
              aria-label={getTigerResource("TIGER_LOGIN_NEW_PASSWORD")}
              type="password"
              autoFocus
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={field}
            />
          </label>
          <label style={labelCol}>
            {getTigerResource("TIGER_LOGIN_CONFIRM_PASSWORD")}
            <input
              aria-label={getTigerResource("TIGER_LOGIN_CONFIRM_PASSWORD")}
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              style={field}
            />
          </label>
          {errors.map((m) => (
            <div key={m} style={errorText}>
              {m}
            </div>
          ))}
          <button type="submit" disabled={busy} style={primaryBtn}>
            {busy
              ? getTigerResource("TIGER_LOGIN_SAVING")
              : getTigerResource("TIGER_LOGIN_RESET")}
          </button>
        </form>
      </div>
    );
  }

  if (view === "sent") {
    return (
      <div style={overlay}>
        <div style={card}>
          {TigerLogo}
          <div>{getTigerResource("TIGER_LOGIN_FORGOT_SENT")}</div>
          <button type="button" style={linkBtn} onClick={() => setView("signin")}>
            {getTigerResource("TIGER_LOGIN_BACK_TO_SIGN_IN")}
          </button>
        </div>
      </div>
    );
  }

  if (view === "forgot") {
    return (
      <div style={overlay}>
        <form onSubmit={onForgotSubmit} style={card}>
          {TigerLogo}
          <h2 style={{ margin: 0, fontWeight: 600, textAlign: "center" }}>
            {getTigerResource("TIGER_LOGIN_FORGOT_TITLE")}
          </h2>
          <div style={{ fontSize: 12, opacity: 0.8 }}>
            {getTigerResource("TIGER_LOGIN_FORGOT_PROMPT")}
          </div>
          <label style={labelCol}>
            {getTigerResource("TIGER_LOGIN_USERNAME_OR_EMAIL")}
            <input
              aria-label={getTigerResource("TIGER_LOGIN_USERNAME_OR_EMAIL")}
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={field}
            />
          </label>
          {error && <div style={errorText}>{error}</div>}
          <button type="submit" disabled={busy} style={primaryBtn}>
            {busy
              ? getTigerResource("TIGER_LOGIN_SENDING")
              : getTigerResource("TIGER_LOGIN_EMAIL_LINK")}
          </button>
          <button type="button" style={linkBtn} onClick={() => setView("signin")}>
            {getTigerResource("TIGER_LOGIN_BACK_TO_SIGN_IN")}
          </button>
        </form>
      </div>
    );
  }

  if (challenge) {
    return (
      <div style={overlay}>
        <form onSubmit={onCodeSubmit} style={card}>
          {TigerLogo}
          <h2 style={{ margin: 0, fontWeight: 600, textAlign: "center" }}>{getTigerResource("TIGER_LOGIN_TWO_FACTOR_TITLE")}</h2>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {getTigerResource("TIGER_LOGIN_AUTH_CODE")}
            <input
              aria-label={getTigerResource("TIGER_LOGIN_AUTH_CODE")}
              inputMode="numeric"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
              style={field}
            />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              aria-label={getTigerResource("TIGER_LOGIN_REMEMBER_DEVICE")}
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
            />
            {getTigerResource("TIGER_LOGIN_REMEMBER_DEVICE")}
          </label>
          {error && <div style={{ color: "#ffb4b4" }}>{error}</div>}
          <button type="submit" disabled={busy} style={primaryBtn}>
            {busy ? getTigerResource("TIGER_LOGIN_VERIFYING") : getTigerResource("TIGER_LOGIN_VERIFY")}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={overlay}>
      <form onSubmit={onPasswordSubmit} style={card}>
        {TigerLogo}
        <h2 style={{ margin: 0, fontWeight: 600, textAlign: "center" }}>{getTigerResource("TIGER_LOGIN_TITLE")}</h2>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {getTigerResource("TIGER_LOGIN_USERNAME")}
          <input
            aria-label={getTigerResource("TIGER_LOGIN_USERNAME")}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
            style={field}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {getTigerResource("TIGER_LOGIN_PASSWORD")}
          <input
            aria-label={getTigerResource("TIGER_LOGIN_PASSWORD")}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={field}
          />
        </label>
        {error && <div style={{ color: "#ffb4b4" }}>{error}</div>}
        <button type="submit" disabled={busy} style={primaryBtn}>
          {busy ? getTigerResource("TIGER_LOGIN_SIGNING_IN") : getTigerResource("TIGER_LOGIN_SIGN_IN")}
        </button>
        {resetSupported && (
          <button
            type="button"
            style={linkBtn}
            onClick={() => {
              setError(null);
              setView("forgot");
            }}
          >
            {getTigerResource("TIGER_LOGIN_FORGOT_PASSWORD")}
          </button>
        )}
        {mode === "mfa_auth" && (
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            {getTigerResource("TIGER_LOGIN_MFA_NOTE")}
          </div>
        )}
      </form>
    </div>
  );
}
